import React, { useState, useEffect } from 'react';
import { getSpecVersions, updateVersionStatus, deleteSpecVersion } from '../utils/api';
import ConfirmModal from './ConfirmModal';
import { useConfirm } from '../hooks/useConfirm';

const VersionManager = ({ specId, specName, onClose, onSelectVersion }) => {
  const { confirm, alert: showAlert, modal } = useConfirm();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [specTitle, setSpecTitle] = useState(null);

  useEffect(() => {
    loadVersions();
  }, [specId]);

  const loadVersions = async () => {
    try {
      const data = await getSpecVersions(specId);
      setVersions(data);
      if (data.length > 0 && data[0].title) {
        setSpecTitle(data[0].title);
      }
    } catch (error) {
      console.error('Failed to load versions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (versionId, status) => {
    try {
      await updateVersionStatus(specId, versionId, status);
      await loadVersions();
    } catch (error) {
      showAlert(error.message || 'Failed to update status', 'Error', 'error');
    }
  };

  const handleDelete = async (versionId) => {
    if (!await confirm({ type: 'warning', title: 'Delete Version?', message: 'This action cannot be undone.' })) return;
    try {
      await deleteSpecVersion(specId, versionId);
      await loadVersions();
    } catch (error) {
      showAlert(error.message || 'Failed to delete version', 'Error', 'error');
    }
  };

  const copyPublicUrl = async (version) => {
    if (!specTitle) {
      showAlert('Spec title not available. Please reload the version manager.', 'Error', 'error');
      return;
    }
    const url = `${window.location.origin}/public/specs/${encodeURIComponent(specTitle)}/${version.version}`;
    navigator.clipboard.writeText(url);
    showAlert('Public URL copied to clipboard!');
  };

  if (loading) return <div style={styles.modal}><div style={styles.content}>Loading...</div></div>;

  return (
    <>
      {modal && <ConfirmModal {...modal} />}
      <div style={styles.modal} onClick={onClose}>
      <div style={styles.content} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
          <h2>Version History</h2>
          <button onClick={onClose} style={styles.closeBtn}>×</button>
        </div>

        <div style={styles.versionList}>
          {versions.length === 0 ? (
            <div style={{textAlign: 'center', padding: '60px 40px', color: '#57606a'}}>
              <div style={{fontSize: '64px', marginBottom: '16px'}}>📦</div>
              <div style={{fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#24292f'}}>No versions yet</div>
              <div style={{fontSize: '14px'}}>Save this spec to create the first version</div>
            </div>
          ) : (
            versions.map(v => (
            <div key={v.id} style={styles.versionCard}>
              <div style={styles.versionHeader}>
                <div>
                  <strong style={styles.versionNumber}>v{v.version}</strong>
                  <span style={styles.date}>{new Date(v.created_at).toLocaleDateString()}</span>
                </div>
                <div style={styles.badges}>
                  {v.is_latest_live === 1 && <span style={styles.badgeLatest}>Latest Live</span>}
                  {v.is_live === 1 && v.is_latest_live !== 1 && <span style={styles.badgeLive}>Live</span>}
                  {v.is_public === 1 && <span style={styles.badgePublic}>Public</span>}
                  {v.is_live !== 1 && <span style={styles.badgeDraft}>Draft</span>}
                </div>
              </div>

              <div style={styles.actions}>
                <button onClick={() => onSelectVersion(v)} style={styles.btn}>
                  {v.is_live === 1 ? 'View' : 'Edit'}
                </button>
                
                {v.is_live !== 1 && (
                  <>
                    <button 
                      onClick={() => handleStatusChange(v.id, { is_live: true, is_latest_live: false, is_public: false })}
                      style={styles.btnSecondary}>
                      Set Live
                    </button>
                    <button 
                      onClick={() => handleStatusChange(v.id, { is_live: true, is_latest_live: true, is_public: false })}
                      style={styles.btnSecondary}>
                      Set Latest Live
                    </button>
                  </>
                )}

                {v.is_live === 1 && (
                  <>
                    <button 
                      onClick={() => handleStatusChange(v.id, { is_live: false, is_latest_live: false, is_public: false })}
                      style={styles.btnWarning}>
                      Unset Live
                    </button>
                    <button 
                      onClick={() => handleStatusChange(v.id, { is_live: true, is_latest_live: v.is_latest_live === 1, is_public: v.is_public !== 1 })}
                      style={styles.btnSecondary}>
                      {v.is_public === 1 ? 'Make Private' : 'Make Public'}
                    </button>
                  </>
                )}

                {v.is_public === 1 && v.is_live === 1 && (
                  <button onClick={() => copyPublicUrl(v)} style={styles.btnSecondary}>
                    Copy Public URL
                  </button>
                )}

                {v.is_live !== 1 && (
                  <button onClick={() => handleDelete(v.id)} style={styles.btnDanger}>
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))
          )}
        </div>
      </div>
    </div>
    </>
  );
};

const styles = {
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 3000,
  },
  content: {
    background: '#fff',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '900px',
    maxHeight: '85vh',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px 32px',
    borderBottom: '1px solid #e1e4e8',
    background: '#f6f8fa',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: '#57606a',
    fontSize: '28px',
    cursor: 'pointer',
    padding: '4px',
    lineHeight: 1,
  },
  versionList: {
    padding: '24px 32px',
    overflowY: 'auto',
    flex: 1,
  },
  versionCard: {
    background: '#fff',
    borderRadius: '8px',
    padding: '20px',
    border: '1px solid #d0d7de',
    marginBottom: '16px',
    transition: 'all 0.2s',
  },
  versionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
  },
  versionNumber: {
    fontSize: '18px',
    color: '#0969da',
    marginRight: '10px',
    fontWeight: '600',
  },
  date: {
    color: '#57606a',
    fontSize: '13px',
  },
  badges: {
    display: 'flex',
    gap: '8px',
  },
  badgeLatest: {
    background: '#4caf50',
    color: '#fff',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  badgeLive: {
    background: '#2196f3',
    color: '#fff',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  badgePublic: {
    background: '#ff9800',
    color: '#fff',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  badgeDraft: {
    background: '#666',
    color: '#fff',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  actions: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  btn: {
    background: '#0969da',
    color: '#fff',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    transition: 'all 0.2s',
  },
  btnSecondary: {
    background: '#f6f8fa',
    color: '#24292f',
    border: '1px solid #d0d7de',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    transition: 'all 0.2s',
  },
  btnWarning: {
    background: '#fb8500',
    color: '#fff',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    transition: 'all 0.2s',
  },
  btnDanger: {
    background: '#cf222e',
    color: '#fff',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    transition: 'all 0.2s',
  },
};

export default VersionManager;
